import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { getPermissions } from "../rbac/hasAccess";
import Text from "../../../utils/Custom/text";
import { CancelButton, SaveAndUpdate } from "../../../utils/Custom/Button";
import { TextFields, DateInput, CheckboxField } from "../../../utils/Custom/custominput";
import CommonTableIR from "../inspectionReport/commonTableIR";
import { applicableDocumentsHeaders, areaofConcernHeaders, detailsItemHeaders, detailsWitnessHeaders, detailsReviewedHeaders, detailsEquipmentInspectionHeaders, INS_ACT_Checkbox_Types, GEN_TextField_Types, defaultApplicableDocuments} from "./generalIRConst";
import IRImageUpload from "../inspectionReport/IRImageUpload";
import { toast } from "react-toastify";
import axiosWithToken from "../../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../../global";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import HseCheckPoints from "./hseCheckPoints";
import { validateRequiredFields } from "../../../utils/commonComponent/errorAlert";
import ImageUploadCommonButton from "../../../utils/commonComponent/ImageUploadCommonButton";


export const makeHandlers = (setter, deletedSetter) => ({
  update: (index, key, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  },
  add: () => setter((prev) => [...prev, { isNew: true }]),
  remove: (index) => {
    setter((prev) => {
      const updated = [...prev];
      const removed = updated.splice(index, 1)[0];
      // only push if it has a real DB id and not already tracked
      if (removed.id) {
        deletedSetter((d) => d.includes(removed.id) ? d : [...d, removed.id]);
      }
      return updated;
    });
  },
});

export const handleTableSubmit = async (rows, originalRows, deletedIds, tableUrl, generalIRId) => {
  // dedupe deletedIds before sending
  const uniqueDeletedIds = [...new Set(deletedIds)];
  if (uniqueDeletedIds.length > 0) {
    await fetchQuery(`DELETE FROM ${tableUrl} WHERE id IN (${uniqueDeletedIds.join(",")})`);
  }

  const newRows = rows.filter((r) => r.isNew);
  const editedRows = rows.filter((r) => !r.isNew && r.id);

  // bulk insert new rows
  if (newRows.length > 0) {
    // collect all keys across all new rows so missing fields become null
    const allKeys = [...new Set(newRows.flatMap((r) => Object.keys(r)))].filter((k) => k !== "isNew");
    const normalizedRows = newRows.map((row) => {
      const { isNew, ...rest } = row;
      const normalized = { generalIRId };
      allKeys.forEach((k) => {
        normalized[k] = rest[k] !== undefined ? rest[k] : null;
      });
      return normalized;
    });
    await axiosWithToken.post(BASE_URL + tableUrl + "/bulk", normalizedRows);
  }
  // patch only changed fields per edited row
  if (editedRows.length > 0) {
    await Promise.all(
      editedRows.map((row) => {
        const original = originalRows.find((r) => r.id === row.id);
        if (!original) return Promise.resolve();
        const changed = Object.fromEntries(
          Object.entries(row).filter(
            ([k, v]) => k !== "isNew" && original[k] !== v
          )
        );
        if (Object.keys(changed).length === 0) return Promise.resolve();
        return axiosWithToken.patch(BASE_URL + tableUrl + `/${row.id}`, changed);
      })
    );
  }
};

const EditGeneralIR = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);

  const [inputStates, setInputStates] = React.useState({
    reportNo: "",
    controlId: "",
    rfiDetails: "",
    inspectionDates: "",
    locationOfInspection: "",
    clientName: "",
    endUser: "",
    project: "",
    contractorNameNumber: "",
    vendor: "",
    poNumberClient: "",
    subVendor: "",
    poNumberVendor: "",
    komPom: 0,
    materialInspection: 0,
    inProcessInspection: 0,
    finalInspection: 0,
    documentReview: 0,
    fatSat: 0,
    wpsPqrWpq: 0,
    preDispatchInspection: 0,
    laboratoryTesting: 0,
    coatingInspection: 0,
    calibration: 0,
    reInspection: 0,
  });

  const [itemDetails, setItemDetails] = React.useState([]);
  const [originalItemDetails, setOriginalItemDetails] = React.useState([]);
  const [deletedItemIds, setDeletedItemIds] = React.useState([]);
  const itemHandlers = makeHandlers(setItemDetails, setDeletedItemIds);

  const [applicableDocRows, setApplicableDocRows] = React.useState(params.id == 0 ? defaultApplicableDocuments : []);
  const [originalApplicableDocRows, setOriginalApplicableDocRows] = React.useState([]);
  const [deletedApplicableDocIds, setDeletedApplicableDocIds] = React.useState([]);
  const applicableDocHandlers = makeHandlers(setApplicableDocRows, setDeletedApplicableDocIds);

  const [areaOfConcernRows, setAreaOfConcernRows] = React.useState([]);
  const [originalAreaOfConcernRows, setOriginalAreaOfConcernRows] = React.useState([]);
  const [deletedAreaOfConcernIds, setDeletedAreaOfConcernIds] = React.useState([]);
  const areaOfConcernHandlers = makeHandlers(setAreaOfConcernRows, setDeletedAreaOfConcernIds);

  const [detailsWitnessRows, setDetailsWitnessRows] = React.useState([]);
  const [originalDetailsWitnessRows, setOriginalDetailsWitnessRows] = React.useState([]);
  const [deletedDetailsWitnessIds, setDeletedDetailsWitnessIds] = React.useState([]);
  const detailsWitnessHandlers = makeHandlers(setDetailsWitnessRows, setDeletedDetailsWitnessIds);

  const [detailsReviewedRows, setDetailsReviewedRows] = React.useState([]);
  const [originalDetailsReviewedRows, setOriginalDetailsReviewedRows] = React.useState([]);
  const [deletedDetailsReviewedIds, setDeletedDetailsReviewedIds] = React.useState([]);
  const detailsReviewedHandlers = makeHandlers(setDetailsReviewedRows, setDeletedDetailsReviewedIds);

  const [detailsEquipmentRows, setDetailsEquipmentRows] = React.useState([]);
  const [originalDetailsEquipmentRows, setOriginalDetailsEquipmentRows] = React.useState([]);
  const [deletedDetailsEquipmentIds, setDeletedDetailsEquipmentIds] = React.useState([]);
  const detailsEquipmentHandlers = makeHandlers(setDetailsEquipmentRows, setDeletedDetailsEquipmentIds);
  
  const [uploadedImages, setUploadedImages] = React.useState([]);
  const [photoRowId, setPhotoRowId] = React.useState(null);

  const updateInputObject = (key, value) => {
    setInputStates((prev) => ({ ...prev, [key]: value }));
  };

  const [hseData, setHseData] = React.useState({
    c1: 0, c2: 0, c3: 0, c4: 0, c5: 0, c6: 0,
    c7: 0, c8: 0, c9: 0, c10: 0, c11: 0, c12: 0,
  });
  const [originalHseData, setOriginalHseData] = React.useState({});
  const [hseId, setHseId] = React.useState(null);

  const updateHse = (key, value) => {
    setHseData((prev) => ({ ...prev, [key]: value }));
  };

  const fetchData = async () => {
    const response = await fetchQuery(`select * from generalIR where id = ${params.id}`);
    setInputStates(response?.[0]);

    const itemResponse = await fetchQuery(`select * from GeneralInspectedItems where generalIRId = ${params.id}`);
    setItemDetails(itemResponse || []);
    setOriginalItemDetails(itemResponse || []);

    const applicableDocResponse = await fetchQuery(`select * from generalReferenceDocuments where generalIRId = ${params.id}`);
    setApplicableDocRows(applicableDocResponse || []);
    setOriginalApplicableDocRows(applicableDocResponse || []);

    const areaOfConcernResponse = await fetchQuery(`select * from generalAreaConcern where generalIRId = ${params.id}`);
    setAreaOfConcernRows(areaOfConcernResponse || []);
    setOriginalAreaOfConcernRows(areaOfConcernResponse || []);

    const witnessResponse = await fetchQuery(`select * from generalInspectionWitnessed where generalIRId = ${params.id}`);
    setDetailsWitnessRows(witnessResponse || []);
    setOriginalDetailsWitnessRows(witnessResponse || []);

    const reviewedResponse = await fetchQuery(`select * from generalReviewedDoc where generalIRId = ${params.id}`);
    setDetailsReviewedRows(reviewedResponse || []);
    setOriginalDetailsReviewedRows(reviewedResponse || []);
    
    const equipmentResponse = await fetchQuery(`select * from generalEquipmentUsed where generalIRId = ${params.id}`);
    setDetailsEquipmentRows(equipmentResponse || []);
    setOriginalDetailsEquipmentRows(equipmentResponse || []);

    const hseResponse = await fetchQuery(`select * from generalHseCheckPoints where generalIRId = ${params.id}`);
    if (hseResponse?.[0]) {
      const { id, generalIRId, ...rest } = hseResponse[0];
      setHseId(id);
      setHseData(rest);
      setOriginalHseData(rest);
    }

    const photoResponse = await fetchQuery(`select * from generalEvaluationPhotos where generalIRId = ${params.id}`);
    if (photoResponse?.[0]) {
      const { id, generalIRId, imagename, imageFilePath } = photoResponse[0];
      setPhotoRowId(id);
      const names = imagename ? imagename.split("||") : [];
      const paths = imageFilePath ? imageFilePath.split(",") : [];
      const parsed = paths.map((path, i) => ({
        imagePath: path,
        imagesName: names[i] || "",
      }));
      setUploadedImages(parsed);
    }

  };

  const onSubmit = async () => {
    // console.log("Payload:", {
    //   ...inputStates,
    //   itemDetails,
    //   deletedItemIds,
    //   applicableDocRows,
    //   deletedApplicableDocIds,
    //   areaOfConcernRows,
    //   deletedAreaOfConcernIds,
    //   detailsWitnessRows,
    //   deletedDetailsWitnessIds,
    //   detailsReviewedRows,
    //   deletedDetailsReviewedIds,
    //   detailsEquipmentRows,
    //   deletedDetailsEquipmentIds,
    //   uploadedImages,
    // });

    const required = [
      { value: inputStates.reportNo, label: "Report No is required" },
    ];
    if (!validateRequiredFields(required)) return;
    const payload = {
      ...inputStates,
      ...(params.id == 0 && {
        createBy: localStorage.getItem("id"),
      }),
    };

    try {
      const Url = BASE_URL + "generalIR";
      let generalIRId = params.id;
      // General Inspection Report
      if (params.id == 0) {
        const res = await axiosWithToken.post(Url, payload);
        generalIRId = res.data.insertId;
        toast.success("Inspection Report Created Successfully!");
      } else {
        await axiosWithToken.patch(Url + `/${params.id}`, payload);
        toast.success("Inspection Report Updated Successfully!");
      }
      // Details of Inspected Items (Fill the data as per PO/Offer list)
      await handleTableSubmit(itemDetails, originalItemDetails, deletedItemIds, "GeneralInspectedItems", generalIRId);
      // Details of Applicable Documents
      await handleTableSubmit(applicableDocRows, originalApplicableDocRows, deletedApplicableDocIds, "generalReferenceDocuments", generalIRId);
      // Details of Area of Concern
      await handleTableSubmit(areaOfConcernRows, originalAreaOfConcernRows, deletedAreaOfConcernIds, "generalAreaConcern", generalIRId);

      // Details of Witness
      await handleTableSubmit(detailsWitnessRows, originalDetailsWitnessRows, deletedDetailsWitnessIds, "generalInspectionWitnessed", generalIRId);

      // Details of Reviewed
      await handleTableSubmit(detailsReviewedRows, originalDetailsReviewedRows, deletedDetailsReviewedIds, "generalReviewedDoc", generalIRId);

      // Details of Equipment
      await handleTableSubmit(detailsEquipmentRows, originalDetailsEquipmentRows, deletedDetailsEquipmentIds, "generalEquipmentUsed", generalIRId);

      const HseUrl = BASE_URL + "generalHseCheckPoints";
      if (hseId) {
        // only patch if something changed vs fetched data
        const originalHse = originalHseData;
        const changedHse = Object.fromEntries(Object.entries(hseData).filter(([k, v]) => originalHse[k] !== v));
        if (Object.keys(changedHse).length > 0) {
          await axiosWithToken.patch(HseUrl + `/${hseId}`, changedHse);
        }
      } else {
        const hseRes = await axiosWithToken.post(HseUrl, { ...hseData, generalIRId });
        setHseId(hseRes.data.insertId);
      }

      const PhotoUrl = BASE_URL + "generalEvaluationPhotos";
      if (uploadedImages.length > 0) {
        const imagename = uploadedImages.map((img) => img.imagesName || "").join("||");
        const imageFilePath = uploadedImages.map((img) => img.imagePath || "").join(",");
        if (photoRowId) {
          await axiosWithToken.patch(PhotoUrl + `/${photoRowId}`, { imagename, imageFilePath });
        } else {
          const photoRes = await axiosWithToken.post(PhotoUrl, { imagename, imageFilePath, generalIRId });
          setPhotoRowId(photoRes.data.insertId);
        }
      }

      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
  if (params.id != 0) {
    fetchData();
  }
}, [params.id]);

  return (
    <>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0
          ? "Create New Inspection Report"
          : "Update Inspection Report"}
      </Text>
      <br />

      <div>
        <TextFields
          label={"Report No"}
          value={inputStates?.reportNo || ""}
          onChange={(e) => updateInputObject("reportNo", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />
        <br/>
        <br/>

        {/* Section 1 & 2 */}
        <div className="border border-gray-400">
          <div className="grid grid-cols-1 md:grid-cols-[65%_35%]">
            <div className="bg-gray-100 font-semibold px-3 py-2 border-b border-gray-400 md:border-r">
              1. General Information
            </div>
            <div className="hidden md:block bg-gray-100 font-semibold px-3 py-2 border-b border-gray-400">
              2. Type of Inspection Activity:
              <span className="font-normal text-xs block">
                (Select the Applicable Stage)
              </span>
            </div>
          </div>

          <div className="hidden md:block">
            {GEN_TextField_Types.map((field, i) => (
              <div key={field.key} className="grid grid-cols-[65%_35%] border-t border-gray-400">
                <div className="flex gap-2 px-3 py-2 border-r border-gray-400">
                  <div className="min-w-[200px]">
                    <span className="font-semibold text-sm">{field.label}</span>
                    {field.subLabel && (
                      <span className="text-xs text-gray-500 block">{field.subLabel}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    {field.isDate ? (
                      <DateInput
                        id={field.key}
                        label={field.label}
                        value={inputStates?.[field.key] || ""}
                        onChange={(value) => updateInputObject(field.key, value)}
                      />
                    ) : (
                    <TextFields
                      value={inputStates?.[field?.key] || ""}
                      onChange={(e) => updateInputObject(field.key, e.target.value)}
                      multiline={!!field.multiline}
                      minRows={1}
                      maxRows={4}
                    />
                    )}
                  </div>
                </div>
                <div className="px-3 py-2 flex items-center">
                  {INS_ACT_Checkbox_Types[i] && (
                    <CheckboxField
                      label={INS_ACT_Checkbox_Types[i].label}
                      value={inputStates?.[INS_ACT_Checkbox_Types[i].key]}
                      onChange={(val) => updateInputObject(INS_ACT_Checkbox_Types[i].key, val)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="md:hidden">
            <div className="border-t border-gray-400">
              {GEN_TextField_Types.map((field) => (
                <div key={field.key} className="flex flex-col gap-1 px-3 py-2 border-b border-gray-400">
                  <div>
                    <span className="font-semibold text-sm">{field.label}</span>
                    {field.subLabel && (
                      <span className="text-xs text-gray-500 block">{field.subLabel}</span>
                    )}
                  </div>
                  <TextFields
                    value={inputStates?.[field.key] || ""}
                    onChange={(e) => updateInputObject(field.key, e.target.value)}
                    multiline={!!field.multiline}
                    minRows={1}
                    maxRows={4}
                  />
                </div>
              ))}
            </div>
            <div className="border-t-2 border-gray-500">
              <div className="bg-gray-100 font-semibold px-3 py-2 border-b border-gray-400 text-sm">
                2. Type of Inspection Activity:
                <span className="font-normal text-xs block">(Select the Applicable Stage)</span>
              </div>
              <div className="grid grid-cols-2 gap-0">
                {INS_ACT_Checkbox_Types.map((act) => (
                  <div key={act.key} className="px-3 py-2 border-b border-r border-gray-400">
                    <CheckboxField
                      label={act.label}
                      value={inputStates?.[act.key]}
                      onChange={(val) => updateInputObject(act.key, val)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <br />

        {/* Section 3 */}
        <CommonTableIR
          headers={detailsItemHeaders}
          rows={itemDetails}
          onChange={itemHandlers.update}
          onDeleteRow={itemHandlers.remove}
          onAddRow={itemHandlers.add}
          label={"Add Item Row"}
          headingLabel={"3. Details of Inspected Items (Fill the data as per PO/Offer list)"}
        />

        <br />

        {/* Section 4 */}
        <CommonTableIR
          headers={applicableDocumentsHeaders}
          rows={applicableDocRows}
          onChange={applicableDocHandlers.update}
          onDeleteRow={applicableDocHandlers.remove}
          onAddRow={applicableDocHandlers.add}
          label={"Add Row"}
          headingLabel={"4. Applicable Reference Documents: (Fill the applicable section and if not mention not applicable):"}
        />

        <br />

        {/* Section 5 */}
        <CommonTableIR
          headers={areaofConcernHeaders}
          rows={areaOfConcernRows}
          onChange={areaOfConcernHandlers.update}
          onDeleteRow={areaOfConcernHandlers.remove}
          onAddRow={areaOfConcernHandlers.add}
          label={"Add Row"}
          headingLabel={"5. Area of Concern (From This Visit & Previous visit, do not delete the concerns after closing):"}
        />

        <br />

        {/* Section 6 */}
        <CommonTableIR
          headers={detailsWitnessHeaders}
          rows={detailsWitnessRows}
          onChange={detailsWitnessHandlers.update}
          onDeleteRow={detailsWitnessHandlers.remove}
          onAddRow={detailsWitnessHandlers.add}
          label={"Add Row"}
          headingLabel={"6. Details of Inspection stages witnessed: (Mention Witnessed activities as per scope – Findings of Inspection)"}
        />

        <br />

        {/* Section 7 */}
        <CommonTableIR
          headers={detailsReviewedHeaders}
          rows={detailsReviewedRows}
          onChange={detailsReviewedHandlers.update}
          onDeleteRow={detailsReviewedHandlers.remove}
          onAddRow={detailsReviewedHandlers.add}
          label={"Add Row"}
          headingLabel={"7. Details of Reviewed Documents: (Mention reviewed documents as per scope with findings)"}
        />

        <br />
        <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3 mb-5">
          8. Result Summary: (Mention Conclusive Result of Inspection here)
        </Text>

        <TextFields
          label={"8. Result Summary: (Mention Conclusive Result of Inspection here)"}
          value={inputStates?.resultSummary || ""}
          onChange={(e) => updateInputObject("resultSummary", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />
        <br />
        <br />

        {/* Section 9 */}
        <CommonTableIR
          headers={detailsEquipmentInspectionHeaders}
          rows={detailsEquipmentRows}
          onChange={detailsEquipmentHandlers.update}
          onDeleteRow={detailsEquipmentHandlers.remove}
          onAddRow={detailsEquipmentHandlers.add}
          label={"Add Row"}
          headingLabel={"9. Details of Equipment(s) / Instrument(s) used for Measuring / Testing during Inspection:"}
        />

        <br />
        <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3 mb-5">
          10. Pending Activities:(Write pending activities from this visit against offered scope / RFI)
        </Text>

        <TextFields
          label={"10. Pending Activities:(Write pending activities from this visit against offered scope / RFI)"}
          value={inputStates?.pendingActivities || ""}
          onChange={(e) => updateInputObject("pendingActivities", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        <br />
        <br />

        <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3 mb-5">
          11. Identification of the Inspected Items: (Mention identification details where stamping has been done)
        </Text>

        <TextFields
          label={"11. Identification of the Inspected Items: (Mention identification details where stamping has been done)"}
          value={inputStates?.identificationOfItems || ""}
          onChange={(e) => updateInputObject("identificationOfItems", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        <br />
        <br />

        <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3 mb-5">
          12. Non-Conformity Report:(Strike through options that are not applicable)
        </Text>

        <TextFields
          label={"12. Non-Conformity Report:(Strike through options that are not applicable)"}
          value={inputStates?.nonConformity || ""}
          onChange={(e) => updateInputObject("nonConformity", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        <br />
        <br />

        <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3 mb-5">
          13. List of Attachments: (Write short description of supporting documents to inspection)
        </Text>

        <TextFields
          label={"13. List of Attachments: (Write short description of supporting documents to inspection)"}
          value={inputStates?.listOfAttachments || ""}
          onChange={(e) => updateInputObject("listOfAttachments", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        <br />
        <br />

        <HseCheckPoints
          data={hseData}
          onChange={updateHse}
          // PrintView={PrintView}
        />

        <br />        

        <>
          <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3 mb-5">
            15. Authorized Signatures:
          </Text>

          <div className="border border-gray-400 text-sm">
            {/* Headers */}
            <div className="grid grid-cols-2">
              <div className="font-semibold text-center px-3 py-2 border-b border-r border-gray-400">
                DYC Global Pvt. Ltd. Surveyor:
              </div>
              <div className="font-semibold text-center px-3 py-2 border-b border-gray-400">
                DYC Global Pvt. Ltd. Reviewer:
              </div>
            </div>

            {/* Signature canvas */}
            <div className="grid grid-cols-2">
              <div className="flex flex-col items-center justify-center px-3 py-4 border-b border-r border-gray-400 min-h-[100px]">
                <ImageUploadCommonButton
                  url={inputStates?.Surveyorsign || ""}
                  updateUrl={(url) => updateInputObject("Surveyorsign", url)}
                  imgShow={true}
                />
              </div>
              <div className="flex flex-col items-center justify-center px-3 py-4 border-b border-gray-400 min-h-[100px]">
                <ImageUploadCommonButton
                  url={inputStates?.ReviewerSign || ""}
                  updateUrl={(url) => updateInputObject("ReviewerSign", url)}
                  imgShow={true}
                />
              </div>
            </div>

            {/* Name and Date */}
            <div className="grid grid-cols-2">
              <div className="grid grid-cols-2 gap-3 px-3 py-2 border-r border-gray-400">
                <TextFields
                  label="Name"
                  value={inputStates?.SurveyorName || ""}
                  onChange={(e) => updateInputObject("SurveyorName", e.target.value)}
                />
                <DateInput
                  label="Date"
                  value={inputStates?.SurveyorDate || null}
                  onChange={(value) => updateInputObject("SurveyorDate", value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 px-3 py-2">
                <TextFields
                  label="Name"
                  value={inputStates?.ReviewerName || ""}
                  onChange={(e) => updateInputObject("ReviewerName", e.target.value)}
                />
                <DateInput
                  label="Date"
                  value={inputStates?.ReviewerDate || null}
                  onChange={(value) => updateInputObject("ReviewerDate", value)}
                />
              </div>
            </div>
          </div>
        </>

        {/* Section 8 */}
        <IRImageUpload
          label="16. Evaluation Photographs:"
          images={uploadedImages}
          setImages={setUploadedImages}
        />

        <br />
      </div>

      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton id="btn-cancel" label="Cancel" outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.create || permissions.allAccess) && (
          <SaveAndUpdate id="btn-add-outline" label="Save" onClick={onSubmit} />
        )}
      </div>
    </>
  );
};

export default EditGeneralIR;